/**
 * Tiny, dependency-free JWT helpers. We only ever read the *unverified* payload
 * to know when a token expires — signature verification is the backend's job.
 * SSR-safe: `atob` is guarded so a server-side call returns null instead of
 * throwing (there's no token on the server anyway).
 */

/** The `exp` claim in ms-since-epoch, or null if the token is unreadable / has no exp. */
export function jwtExpiryMs(token: string | null | undefined): number | null {
  if (!token || typeof atob !== 'function') return null;
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4)));
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * True when the token is definitively past its `exp` (with a small clock-skew
 * allowance). A token whose `exp` can't be read returns false — we don't tear a
 * session down over an unparseable claim; the API's 401 remains the backstop.
 */
export function isJwtExpired(token: string | null | undefined, skewMs = 10_000): boolean {
  const exp = jwtExpiryMs(token);
  if (exp == null) return false;
  return Date.now() >= exp - skewMs;
}
